/**
 * Copyright(c) Live2D Inc. All rights reserved.
 *
 * Use of this source code is governed by the Live2D Open Software license
 * that can be found at https://www.live2d.com/eula/live2d-open-software-license-agreement_en.html.
 */

import { CubismMatrix44 } from './framework/math/cubismmatrix44';
import { CubismViewMatrix } from './framework/math/cubismviewmatrix';

import * as LAppDefine from './lappdefine';
import { canvas, gl, LAppDelegate } from './lappdelegate';
import { LAppLive2DManager } from './lapplive2dmanager';
import { LAppPal } from './lapppal';
import { LAppSprite } from './lappsprite';
import { TextureInfo } from './lapptexturemanager';
import { TouchManager } from './touchmanager';

/**
 * 描画クラス。
 */
export class LAppView {
  /**
   * コンストラクタ
   */
  constructor() {
    // タッチ関係のイベント管理
    this._touchManager = new TouchManager();

    // デバイス座標からスクリーン座標に変換するための
    this._deviceToScreen = new CubismMatrix44();

    // 画面の表示の拡大縮小や移動の変換を行う行列
    this._viewMatrix = new CubismViewMatrix();
  }

  /**
   * 初期化する。
   */
  public initialize(options: any): void {
    const { width, height } = canvas;
    this._options = options;
    const ratio: number = width / height;
    const left: number = -ratio;
    const right: number = ratio;
    const bottom: number = LAppDefine.ViewLogicalLeft;
    const top: number = LAppDefine.ViewLogicalRight;
    this._viewMatrix.setScreenRect(left, right, bottom, top); // デバイスに対応する画面の範囲。 Xの左端、Xの右端、Yの下端、Yの上端
    this._viewMatrix.scale(LAppDefine.ViewScale, LAppDefine.ViewScale);

    this._deviceToScreen.loadIdentity();
    if (width > height) {
      const screenW: number = Math.abs(right - left);
      this._deviceToScreen.scaleRelative(screenW / width, -screenW / width);
    } else {
      const screenH: number = Math.abs(top - bottom);
      this._deviceToScreen.scaleRelative(screenH / height, -screenH / height);
    }
    this._deviceToScreen.translateRelative(-width * 0.5, -height * 0.5);

    // 表示範囲の設定
    this._viewMatrix.setMaxScale(LAppDefine.ViewMaxScale); // 限界拡張率
    this._viewMatrix.setMinScale(LAppDefine.ViewMinScale); // 限界縮小率

    // 表示できる最大範囲
    this._viewMatrix.setMaxScreenRect(
      LAppDefine.ViewLogicalMaxLeft,
      LAppDefine.ViewLogicalMaxRight,
      LAppDefine.ViewLogicalMaxBottom,
      LAppDefine.ViewLogicalMaxTop
    );
  }

  /**
   * 解放する
   */
  public release(): void {
    this._viewMatrix = null;
    this._touchManager = null;
    this._deviceToScreen = null;
  }

  /**
   * 描画する。
   */
  public render(): void {

    gl.flush();

    const live2DManager: LAppLive2DManager = LAppLive2DManager.getInstance();

    live2DManager.setViewMatrix(this._viewMatrix);

    live2DManager.onUpdate();
  }


  /**
   * タッチされた時に呼ばれる。
   *
   * @param pointX スクリーンX座標
   * @param pointY スクリーンY座標
   */
  public onTouchesBegan(pointX: number, pointY: number): void {
    this._touchManager.touchesBegan(pointX, pointY);
  }

  /**
   * タッチしているときにポインタが動いたら呼ばれる。
   *
   * @param pointX スクリーンX座標
   * @param pointY スクリーンY座標
   */
  public onTouchesMoved(pointX: number, pointY: number, rect: DOMRect): void {
    const sx: number = this.transformScreenX(pointX - rect.left); //sx
    const sy: number = this.transformScreenY(pointY - rect.top);  //sy
    const target = this.transformRect({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height * (this._options.headPos || 0.5)
    }, {
      x: pointX,
      y: pointY
    }, rect);
    const viewX: number = this.transformViewX(target.x - rect.left);
    const viewY: number = this.transformViewY(target.y - rect.top);
    if (LAppDefine.DebugTouchLogEnable) {
      console.log("转换之后的显示坐标[X,Y]:", viewX, viewY);
      console.log("转换之后的屏幕坐标[X,Y]:", sx, sy);
    }
    this._touchManager.touchesMoved(sx, sy);
    const live2DManager: LAppLive2DManager = LAppLive2DManager.getInstance();
    live2DManager.onDrag(viewX, viewY);
  }

  /**
   * タッチが終了したら呼ばれる。
   *
   * @param pointX スクリーンX座標
   * @param pointY スクリーンY座標
   */
  public onTouchesEnded(pointX: number, pointY: number): void {
    // タッチ終了
    const live2DManager: LAppLive2DManager = LAppLive2DManager.getInstance();
    live2DManager.onDrag(0.0, 0.0);

    {
      // シングルタップ
      const x: number = this._deviceToScreen.transformX(pointX); // 論理座標変換した座標を取得。
      const y: number = this._deviceToScreen.transformY(pointY); // 論理座標変化した座標を取得。

      if (LAppDefine.DebugTouchLogEnable) {
        LAppPal.printMessage(`[APP]touchesEnded x: ${x} y: ${y}`);
      }
      live2DManager.onTap(x, y);
    }
  }

  /**
   * 主动触发点击.model3.json中Motions的数组
   * @param group Motions的数组名称 例如Idle
   * @param index 数组序列
   */
  public onMoveOver(group: string, index: number):void{
    const live2DManager: LAppLive2DManager = LAppLive2DManager.getInstance();
    live2DManager.onDrag(0.0, 0.0);
    live2DManager.onSelector(group, index);
  }

  /**
   * X座標をView座標に変換する。
   *
   * @param deviceX デバイスX座標
   */
  public transformViewX(deviceX: number): number {
    const screenX: number = this._deviceToScreen.transformX(deviceX); // 論理座標変換した座標を取得。
    return this._viewMatrix.invertTransformX(screenX); // 拡大、縮小、移動後の値。
  }

  /**
   * Y座標をView座標に変換する。
   *
   * @param deviceY デバイスY座標
   */
  public transformViewY(deviceY: number): number {
    const screenY: number = this._deviceToScreen.transformY(deviceY); // 論理座標変換した座標を取得。
    return this._viewMatrix.invertTransformY(screenY);
  }

  /**
   * X座標をScreen座標に変換する。
   * @param deviceX デバイスX座標
   */
  public transformScreenX(deviceX: number): number {
    return this._deviceToScreen.transformX(deviceX);
  }

  /**
   * Y座標をScreen座標に変換する。
   *
   * @param deviceY デバイスY座標
   */
  public transformScreenY(deviceY: number): number {
    return this._deviceToScreen.transformY(deviceY);
  }

  public static dot(A: any, B: any) {
    return A.x * B.x + A.y * B.y;
  }

  public static normalize(x: number, y: number) {
    let length = Math.sqrt(x * x + y * y)
    return {
      x: x / length,
      y: y / length
    }
  }

  public transformRect(center: any, transform: any, rect: DOMRect) {
    if (transform.x < rect.left + rect.width && transform.y < rect.top + rect.height &&
      transform.x > rect.left && transform.y > rect.top) return transform;
    let Len_X = center.x - transform.x;
    let Len_Y = center.y - transform.y;

    function angle(Len_X: number, Len_Y: number) {
      return Math.acos(LAppView.dot({
        x: 0,
        y: 1
      }, LAppView.normalize(Len_X, Len_Y))) * 180 / Math.PI
    }

    let angleTarget = angle(Len_X, Len_Y);
    if (transform.x < center.x) angleTarget = 360 - angleTarget;
    let angleLeftTop = 360 - angle(rect.left - center.x, (rect.top - center.y) * -1);
    let angleLeftBottom = 360 - angle(rect.left - center.x, (rect.top + rect.height - center.y) * -1);
    let angleRightTop = angle(rect.left + rect.width - center.x, (rect.top - center.y) * -1);
    let angleRightBottom = angle(rect.left + rect.width - center.x, (rect.top + rect.height - center.y) * -1);
    let scale = Len_Y / Len_X;
    let res = {};

    if (angleTarget < angleRightTop) {
      let y3 = rect.top - center.y;
      let x3 = y3 / scale;
      res = {
        y: center.y + y3,
        x: center.x + x3
      }
    } else if (angleTarget < angleRightBottom) {
      let x3 = rect.left + rect.width - center.x;
      let y3 = x3 * scale;
      res = {
        y: center.y + y3,
        x: center.x + x3
      }
    } else if (angleTarget < angleLeftBottom) {
      let y3 = rect.top + rect.height - center.y;
      let x3 = y3 / scale;
      res = {
        y: center.y + y3,
        x: center.x + x3
      }
    } else if (angleTarget < angleLeftTop) {
      let x3 = center.x - rect.left;
      let y3 = x3 * scale;
      res = {
        y: center.y - y3,
        x: center.x - x3
      }
    } else {
      let y3 = rect.top - center.y;
      let x3 = y3 / scale;
      res = {
        y: center.y + y3,
        x: center.x + x3
      }
    }

    return res;
  }

  _touchManager: TouchManager; // タッチマネージャー
  _deviceToScreen: CubismMatrix44; // デバイスからスクリーンへの行列
  _viewMatrix: CubismViewMatrix; // viewMatrix
  _changeModel: boolean; // モデル切り替えフラグ
  _isClick: boolean; // クリック中
  _options: any;
}
